import { useQuery } from '@tanstack/react-query';

import { api } from './api';

/**
 * Public site settings — spec §6.18. A flat `dotted.key => value` map of the rows flagged
 * public on the server (contact details, socials, the app-block mode, SEO defaults, partner
 * logos).
 *
 * Fetched once and held effectively forever: settings change rarely, and every consumer is
 * chrome (footer, Home) that would otherwise refetch on each mount.
 */
export type PublicSettings = Record<string, unknown>;

export type AppSectionMode = 'hidden' | 'coming_soon' | 'live';

export function useSettings() {
  return useQuery({
    queryKey: ['settings'],
    queryFn: async () => (await api.get<{ data: PublicSettings }>('/settings')).data.data,
    staleTime: Infinity,
  });
}

/** Read a string setting with a fallback, so the UI never renders `undefined`. */
export function settingString(settings: PublicSettings | undefined, key: string, fallback = ''): string {
  const value = settings?.[key];
  return typeof value === 'string' && value !== '' ? value : fallback;
}

export type PartnerLogo = { name: string; url: string };

export function partnerLogos(settings: PublicSettings | undefined): PartnerLogo[] {
  const value = settings?.['homepage.partner_logos'];
  return Array.isArray(value) ? (value as PartnerLogo[]) : [];
}

/**
 * The Home app block's mode (§27.8). Defaults to `hidden` — matching the server default — so
 * a failed settings fetch can never ship dead App Store / Google Play links.
 */
export function appSectionMode(settings: PublicSettings | undefined): AppSectionMode {
  const value = settings?.['app.section_mode'];
  return value === 'live' || value === 'coming_soon' ? value : 'hidden';
}

/** Social links, in the order the design shows them; empty URLs are dropped by the caller. */
export function socialLinks(settings: PublicSettings | undefined): { key: string; url: string }[] {
  return (['x', 'facebook', 'instagram', 'linkedin'] as const).map((key) => ({
    key,
    url: settingString(settings, `social.${key}`),
  }));
}
